import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { collection, query, where, getDocs, doc, setDoc, deleteDoc, serverTimestamp, onSnapshot, arrayUnion } from 'firebase/firestore';
import { db } from './firebase';
import { useSimpleAuth } from './SimpleAuthContext';
import { Budget, BudgetEntry, Project, ProjectEntry, ProjectStatus, ProjectFilters, BudgetSubject, BudgetSection, MeasureType, IntervalType } from '../types/budget.types';

// Company type from original FedermanBenjamin
interface Company {
  id: string;
  CompanyName: string;  // Original field name is CompanyName, not Name
  Users: string[];
  CreatedDate?: Date;
  Owner?: string;
  ContactPerson?: string;
  ContactEmail?: string;
  ContactPhone?: string;
  Active?: boolean;
}

interface BudgetContextType {
  // Company
  companies: Company[];
  currentCompany: Company | null;
  selectCompany: (companyId: string) => void;

  // Data
  budgets: Budget[];
  currentBudget: Budget | null;
  entries: BudgetEntry[];
  projects: Project[];

  // Loading states
  isLoading: boolean;
  isSaving: boolean;

  // Budget CRUD
  selectBudget: (budgetId: string) => void;
  createBudget: (budget: Omit<Budget, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>) => Promise<string>;
  updateBudget: (budgetId: string, updates: Partial<Budget>) => Promise<void>;
  deleteBudget: (budgetId: string) => Promise<void>;

  // Entry CRUD
  createEntry: (entry: Omit<BudgetEntry, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateEntry: (entryId: string, updates: Partial<BudgetEntry>) => Promise<void>;
  deleteEntry: (entryId: string) => Promise<void>;
  getEntriesForPeriod: (period: string) => BudgetEntry[];

  // Subject & Section management
  addSubject: (subject: Omit<BudgetSubject, 'id'>) => void;
  updateSubject: (subjectId: string, updates: Partial<BudgetSubject>) => void;
  removeSubject: (subjectId: string) => void;
  addSection: (section: Omit<BudgetSection, 'id'>) => void;
  updateSection: (sectionId: string, updates: Partial<BudgetSection>) => void;
  removeSection: (sectionId: string) => void;

  // Project CRUD
  createProject: (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateProject: (projectId: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  regenerateProjectData: (projectId: string, preserveExisting?: boolean) => Promise<void>;

  // Project Subject & Section management
  addProjectSubject: (projectId: string, subject: Omit<BudgetSubject, 'id'>) => Promise<void>;
  updateProjectSubject: (projectId: string, subjectId: string, updates: Partial<BudgetSubject>) => Promise<void>;
  removeProjectSubject: (projectId: string, subjectId: string) => Promise<void>;
  addProjectSection: (projectId: string, section: Omit<BudgetSection, 'id'>) => Promise<void>;
  updateProjectSection: (projectId: string, sectionId: string, updates: Partial<BudgetSection>) => Promise<void>;
  removeProjectSection: (projectId: string, sectionId: string) => Promise<void>;
  reorderProjectSubjects: (projectId: string, subjects: BudgetSubject[]) => Promise<void>;
  getProjectSubjects: (projectId: string) => { subjects: BudgetSubject[]; sections: BudgetSection[] };

  // Project entries
  projectEntries: ProjectEntry[];
  updateProjectEntries: (projectId: string, entries: Array<{ period: string; subjectId: string; sectionId?: string; measure: string; amount: number }>) => Promise<void>;

  // Project filtering
  projectFilters: ProjectFilters;
  setProjectFilters: (filters: Partial<ProjectFilters>) => void;
  filteredProjects: Project[];

  // Project calculations
  calculateProjectProgress: (project: Project) => {
    timeConsumed: number;
    budgetConsumed: number;
    invoicesIssued: number;
    profit: number;
    profitPercent: number;
  };

  // Calculations
  calculateSummary: (period?: string) => { totalIncome: number; totalExpenses: number; profit: number };
  getPeriodsList: () => string[];

  // Data generation
  createDataCollection: () => Promise<void>;

  // Filters
  selectedPeriod: string | null;
  setSelectedPeriod: (period: string | null) => void;
  selectedMeasures: MeasureType[];
  setSelectedMeasures: (measures: MeasureType[]) => void;
}

const BudgetContext = createContext<BudgetContextType | null>(null);

const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const BudgetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { simpleUser, isAuthenticated } = useSimpleAuth();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [currentBudget, setCurrentBudget] = useState<Budget | null>(null);
  const [entries, setEntries] = useState<BudgetEntry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectEntries, setProjectEntries] = useState<ProjectEntry[]>([]);
  const [projectFilters, setProjectFiltersState] = useState<ProjectFilters>({
    search: '',
    status: 'all',
    projectManager: '',
    manager: '',
    client: '',
    contract: '',
    dateFrom: '',
    dateTo: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  const [selectedMeasures, setSelectedMeasures] = useState<MeasureType[]>(['budget', 'actual', 'goal']);

  // Load companies where user email is in Users array (original FedermanBenjamin pattern)
  useEffect(() => {
    if (!isAuthenticated || !simpleUser?.email) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const companiesRef = collection(db, 'companies');
    const q = query(companiesRef, where('Users', 'array-contains', simpleUser.email));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const companiesList = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,  // Must come AFTER spread to override any 'id' field in data
      })) as Company[];

      setCompanies(companiesList);

      // Auto-select first company
      if (!currentCompany && companiesList.length > 0) {
        setCurrentCompany(companiesList[0]);
      }

      setIsLoading(false);
    }, (error) => {
      console.error('Error loading companies:', error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [isAuthenticated, simpleUser?.email]);

  // Load budgets from current company's subcollection
  useEffect(() => {
    if (!currentCompany?.id) {
      setBudgets([]);
      setCurrentBudget(null);
      return;
    }

    // Original path: companies/{companyId}/Budgets
    const budgetsRef = collection(db, 'companies', currentCompany.id, 'Budgets');

    const unsubscribe = onSnapshot(budgetsRef, (snapshot) => {
      const budgetsList = snapshot.docs.map(doc => {
        const data = doc.data();

        // Parse subjects from original format (may have Name/Type fields)
        // Type mapping: Credit = income, Debit = expense, Calced = calculated
        const subjects = (data.Subjects || data.subjects || []).map((s: any, idx: number) => {
          const name = s.Name || s.name || '';
          let type = (s.Type || s.type || '').toLowerCase();

          // Map original types to our types, or infer from Hebrew name
          if (type === 'credit' || name === 'הכנסות' || name.includes('Income')) {
            type = 'credit';
          } else if (type === 'calced' || name === 'רווח' || name.includes('Profit')) {
            type = 'calculated';
          } else {
            // Default to debit for expenses (הוצאות...)
            type = 'debit';
          }

          return {
            id: s.id || `subject-${idx}`,
            name,
            type: type as 'credit' | 'debit' | 'calculated' | 'accumulative',
            order: s.Order || s.order || idx,
            // Vue stores total income on Credit subjects in IncomePartGoal field
            goalPercentage: s.IncomePartGoal || s.incomePartGoal || s.GoalPercentage || s.goalPercentage || 0,
          };
        });

        // Parse sections from subjects (Vue stores sections nested inside subjects)
        const sections: any[] = [];
        (data.Subjects || data.subjects || []).forEach((subj: any, subjIdx: number) => {
          const subjectId = subj.id || `subject-${subjIdx}`;
          (subj.Sections || subj.sections || []).forEach((s: any, secIdx: number) => {
            sections.push({
              id: s.id || `section-${subjIdx}-${secIdx}`,
              name: s.Name || s.name || '',
              subjectId: subjectId,
              order: s.Order || s.order || secIdx,
              incomePartGoal: s.IncomePartGoal || s.incomePartGoal || 0,
            });
          });
        });

        // Parse measures - Vue stores as [{Name: 'בפועל'}, {Name: 'מתוכנן'}]
        const rawMeasures = data.Measures || data.measures || ['budget', 'actual'];
        const measures = rawMeasures.map((m: any) =>
          typeof m === 'string' ? m : (m.Name || m.name || 'budget')
        );

        return {
          id: doc.id,
          name: data.Name || data.name || data.Document?.Name || 'Unnamed Budget',
          description: data.Description || data.description,
          intervalType: (data.IntervalType || data.intervalType || 'monthly').toLowerCase() as IntervalType,
          startDate: (() => {
            // Vue app stores dates in DateRange.Start / DateRange.End format
            const d = data.DateRange?.Start || data.StartDate?.toDate?.() || data.startDate?.toDate?.() || data.StartDate || data.startDate;
            if (!d) return '';
            if (d.toDate) return d.toDate().toISOString().slice(0, 10);
            if (d instanceof Date) return d.toISOString().slice(0, 10);
            if (typeof d === 'string') return d.slice(0, 10);
            return '';
          })(),
          endDate: (() => {
            // Vue app stores dates in DateRange.Start / DateRange.End format
            const d = data.DateRange?.End || data.EndDate?.toDate?.() || data.endDate?.toDate?.() || data.EndDate || data.endDate;
            if (!d) return '';
            if (d.toDate) return d.toDate().toISOString().slice(0, 10);
            if (d instanceof Date) return d.toISOString().slice(0, 10);
            if (typeof d === 'string') return d.slice(0, 10);
            return '';
          })(),
          subjects,
          sections,
          measures,
          currency: data.Currency || data.currency || '', // Empty means no symbol
          isActive: data.Active !== false && data.isActive !== false,
          createdAt: data.CreatedDate?.toDate?.() || data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.UpdatedDate?.toDate?.() || data.updatedAt?.toDate?.() || new Date(),
          createdBy: data.CreatedBy || data.createdBy || '',
          organizationId: currentCompany.id,
          // Store raw data for entries parsing
          _rawData: data._Data,
        } as Budget & { _rawData?: string };
      });

      setBudgets(budgetsList);

      // Auto-select first active budget
      if (budgetsList.length > 0) {
        const activeBudget = budgetsList.find(b => b.isActive) || budgetsList[0];
        setCurrentBudget(activeBudget);
      } else {
        setCurrentBudget(null);
      }
    }, (error) => {
      console.error('Error loading budgets:', error);
    });

    return () => unsubscribe();
  }, [currentCompany?.id]);

  // Parse entries from current budget's _Data field
  // Original FedermanBenjamin format: { week, subject, section, בפועל (Actual), מתוכנן (Budget) }
  useEffect(() => {
    if (!currentBudget) {
      setEntries([]);
      return;
    }

    const budgetWithRaw = currentBudget as Budget & { _rawData?: string };

    if (!budgetWithRaw._rawData) {
      setEntries([]);
      return;
    }

    try {
      const rawEntries = JSON.parse(budgetWithRaw._rawData);

      // Create a map of subject names to subject IDs
      const subjectNameToId = new Map<string, string>();
      currentBudget.subjects.forEach(s => {
        subjectNameToId.set(s.name, s.id);
      });

      // Convert to our BudgetEntry format
      // Each raw entry has both בפועל (Actual) and מתוכנן (Budget), so we create two entries
      const entriesList: BudgetEntry[] = [];

      // Determine period field based on interval type
      // Vue app uses: d[IntervalType.toLowerCase().replace('ly', '')]
      // So 'monthly' -> 'month', 'weekly' -> 'week'
      const intervalType = currentBudget.intervalType || 'monthly';
      const periodField = intervalType.toLowerCase().replace('ly', '');

      rawEntries.forEach((entry: any, idx: number) => {
        const subjectName = entry.subject || '';
        const sectionName = entry.section || '';
        // Use the correct period field based on interval type
        const period = entry[periodField]?.toString() || entry.week?.toString() || entry.month?.toString() || '0';

        // Find subject ID by name
        const subjectId = subjectNameToId.get(subjectName) || subjectName;

        // Parse values for each measure (Hebrew: בפועל = Actual, מתוכנן = Budget/Planned)
        // Use the actual measure names from the budget for proper matching
        const measures = currentBudget.measures || ['בפועל', 'מתוכנן'];

        measures.forEach((measureName, mIdx) => {
          // Try both the exact measure name and common Hebrew names
          let value = parseFloat(entry[measureName]);
          if (isNaN(value)) {
            // Fallback for common Hebrew measure names
            if (measureName === 'actual' || measureName.includes('בפועל')) {
              value = parseFloat(entry['בפועל']) || 0;
            } else if (measureName === 'budget' || measureName.includes('מתוכנן')) {
              value = parseFloat(entry['מתוכנן']) || 0;
            } else {
              value = 0;
            }
          }

          entriesList.push({
            id: `entry-${idx}-${mIdx}`,
            budgetId: currentBudget.id,
            subjectId,
            sectionId: sectionName,
            period,
            measure: measureName,
            amount: value,
            createdAt: new Date(),
            updatedAt: new Date(),
            // Keep original fields
            subject: subjectName,
            section: sectionName,
          } as BudgetEntry);
        });
      });

      setEntries(entriesList);
    } catch (error) {
      console.error('Error parsing budget data:', error);
      setEntries([]);
    }
  }, [currentBudget]);

  // Load projects for current company (projects are independent entities, not children of budgets)
  useEffect(() => {
    if (!currentCompany?.id) {
      setProjects([]);
      setProjectEntries([]);
      return;
    }

    // Projects are stored at company level: companies/{companyId}/Projects
    const projectsRef = collection(db, 'companies', currentCompany.id, 'Projects');

    const unsubscribe = onSnapshot(projectsRef, (snapshot) => {
      const allProjectEntries: ProjectEntry[] = [];

      const projectsList = snapshot.docs.map(doc => {
        const data = doc.data();

        // Debug: Log all project fields to understand data structure
        console.log(`[BudgetContext] Project "${data.Document?.Name}" ALL DATA:`, JSON.stringify(data, null, 2));

        // Map Vue Status number to our ProjectStatus type
        // Vue: 1=Active, 2=Completed, 3=Future
        let status: ProjectStatus = 'active';
        if (data.Status === 2 || data.status === 'completed') status = 'completed';
        else if (data.Status === 3 || data.status === 'future') status = 'future';
        else if (data.Status === 1 || data.status === 'active') status = 'active';

        // Parse dates from Vue format (DateRange.Start/End or direct fields)
        const parseDate = (d: any): string => {
          if (!d) return '';
          if (d.toDate) return d.toDate().toISOString().slice(0, 10);
          if (d instanceof Date) return d.toISOString().slice(0, 10);
          if (typeof d === 'string') return d.slice(0, 10);
          return '';
        };

        const startDate = parseDate(data.DateRange?.Start || data.StartDate || data.startDate);
        const endDate = parseDate(data.DateRange?.End || data.EndDate || data.endDate);

        // Parse project entries from _Data field
        // Vue format: { subject, section, month/week, "בפועל": value, "מתוכנן": value }
        // Each row has multiple measures as separate fields
        if (data._Data) {
          try {
            let rawData = data._Data;
            // Handle double-stringified JSON
            if (typeof rawData === 'string') {
              rawData = JSON.parse(rawData);
              if (typeof rawData === 'string') {
                rawData = JSON.parse(rawData);
              }
            }
            const rawEntries = Array.isArray(rawData) ? rawData : [];

            // Standard non-measure fields
            const metaFields = ['subject', 'section', 'month', 'week', 'period', 'id'];

            // Map Hebrew measure names to English
            const normalizeMeasure = (name: string): string => {
              const lower = name.toLowerCase();
              if (name === 'בפועל' || lower === 'actual') return 'actual';
              if (name === 'מתוכנן' || lower === 'budget' || lower === 'planned') return 'budget';
              if (name === 'יעד' || lower === 'goal') return 'goal';
              return name; // Keep original for unknown measures
            };

            // Debug: Log first entry to help diagnose data format
            if (rawEntries.length > 0) {
              console.log('[BudgetContext] Sample project entry format:', rawEntries[0]);
              console.log('[BudgetContext] Total project entries to parse:', rawEntries.length);
            }

            rawEntries.forEach((entry: any, idx: number) => {
              // Get period (month or week based on budget interval type)
              const period = entry.month?.toString() || entry.week?.toString() || entry.period?.toString() || '';
              const subjectId = entry.subject || '';
              const sectionId = entry.section || '';

              // Find all measure fields (any field that's not a meta field and has a numeric value)
              const measureFields = Object.keys(entry).filter(k =>
                !metaFields.includes(k.toLowerCase()) &&
                (typeof entry[k] === 'number' || !isNaN(parseFloat(entry[k])))
              );

              // Create a ProjectEntry for each measure
              measureFields.forEach((measureName, mIdx) => {
                const amount = parseFloat(entry[measureName]) || 0;
                if (amount !== 0 || measureFields.length > 0) {
                  allProjectEntries.push({
                    id: `${doc.id}-entry-${idx}-${mIdx}`,
                    projectId: doc.id,
                    period,
                    subjectId,
                    sectionId,
                    measure: normalizeMeasure(measureName),
                    amount,
                  });
                }
              });
            });
          } catch (e) {
            console.error('Error parsing project entries:', e, data._Data);
          }
        }

        // Debug: Log parsed entries count for this project
        const projectEntriesCount = allProjectEntries.filter(e => e.projectId === doc.id).length;
        if (projectEntriesCount > 0) {
          console.log(`[BudgetContext] Parsed ${projectEntriesCount} entries for project "${data.Document?.Name || doc.id}"`);
        }

        // Parse project Subjects (Vue stores project-specific subjects with goals)
        const rawProjectSubjects = data.Subjects || [];

        // Calculate budget from Credit subjects' IncomePartGoal (Vue formula)
        // Budget = sum of IncomePartGoal from Credit-type subjects
        const calculatedBudget = rawProjectSubjects
          .filter((s: any) => s.Type === 'Credit')
          .map((s: any) => parseFloat(s.IncomePartGoal) || 0)
          .reduce((sum: number, val: number) => sum + val, 0);

        // Parse subjects into our BudgetSubject format
        const parsedSubjects: BudgetSubject[] = rawProjectSubjects.map((s: any, idx: number) => ({
          id: s.id || `subject-${idx}`,
          name: s.Name || '',
          type: s.Type === 'Credit' ? 'credit' as const : s.Type === 'Calced' ? 'calculated' as const : 'debit' as const,
          order: s.Order || idx,
          goalPercentage: parseFloat(s.IncomePartGoal) || 0,
        }));

        // Parse sections from subjects (Vue stores sections nested inside subjects)
        const parsedSections: BudgetSection[] = [];
        rawProjectSubjects.forEach((subj: any, subjIdx: number) => {
          const subjectId = subj.id || `subject-${subjIdx}`;
          (subj.Sections || []).forEach((sec: any, secIdx: number) => {
            parsedSections.push({
              id: sec.id || `section-${subjIdx}-${secIdx}`,
              name: sec.Name || '',
              subjectId: subjectId,
              order: sec.Order || secIdx,
              incomePartGoal: parseFloat(sec.IncomePartGoal) || 0,
            });
          });
        });

        // Parse measures
        const parsedMeasures = (data.Measures || []).map((m: any) =>
          typeof m === 'string' ? m : (m.Name || m.name || 'budget')
        );

        // Parse goals from Vue format - either from Goals array or from Subjects
        let goals = (data.Goals || data.goals || []).map((g: any) => ({
          subjectName: g.Subject || g.subjectName || g.subject || '',
          sectionName: g.Section || g.sectionName || g.section || '',
          goalPercent: g.GoalPercent || g.goalPercent || g.IncomePartGoal || 0,
        }));

        // If no explicit goals, derive from project Subjects
        if (goals.length === 0 && rawProjectSubjects.length > 0) {
          rawProjectSubjects.forEach((subj: any) => {
            const sections = subj.Sections || [];
            if (sections.length > 0) {
              sections.forEach((sec: any) => {
                goals.push({
                  subjectName: subj.Name || '',
                  sectionName: sec.Name || '',
                  goalPercent: parseFloat(sec.IncomePartGoal) || 0,
                });
              });
            } else {
              goals.push({
                subjectName: subj.Name || '',
                sectionName: undefined,
                goalPercent: parseFloat(subj.IncomePartGoal) || 0,
              });
            }
          });
        }

        return {
          id: doc.id,
          name: data.Document?.Name || data.Name || data.name || 'Unnamed Project',
          description: data.Description || data.description || '',
          organizationId: currentCompany.id,
          budgetId: data.BudgetId || data.budgetId || undefined, // Optional reference to a budget
          status,
          startDate,
          endDate,
          budget: calculatedBudget || parseFloat(data.Budget || data.budget) || 0,
          // Vue uses: PManager = Project Manager (מנהל פרוייקט), PM = Manager (מנהל)
          projectManager: data.PManager || data.projectManager || '',
          manager: data.PM || data.manager || '',
          client: data.Client || data.client || '',
          contractNumber: data.Contract || data.contractNumber || '',
          // Project-specific structure
          subjects: parsedSubjects,
          sections: parsedSections,
          measures: parsedMeasures,
          intervalType: (data.IntervalType || 'monthly').toLowerCase() as IntervalType,
          currency: data.Currency || data.currency || 'ILS',
          goals,
          createdAt: data.CreatedDate?.toDate?.() || data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.UpdatedDate?.toDate?.() || data.updatedAt?.toDate?.() || new Date(),
        } as Project;
      });

      setProjects(projectsList);
      setProjectEntries(allProjectEntries);
    }, (error) => {
      console.error('Error loading projects:', error);
    });

    return () => unsubscribe();
  }, [currentCompany?.id]); // Projects load at company level, not dependent on budget

  const selectCompany = useCallback((companyId: string) => {
    const company = companies.find(c => c.id === companyId);
    if (company) {
      setCurrentCompany(company);
      setCurrentBudget(null);
    }
  }, [companies]);

  const selectBudget = useCallback((budgetId: string) => {
    const budget = budgets.find(b => b.id === budgetId);
    if (budget) {
      setCurrentBudget(budget);
    }
  }, [budgets]);

  const createBudget = useCallback(async (budget: Omit<Budget, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>): Promise<string> => {
    if (!currentCompany?.id || !simpleUser?.uid) throw new Error('Not authenticated or no company selected');

    const id = generateId();
    const subjects = budget.subjects || [];
    const sections = budget.sections || [];

    // Convert to Vue format with nested sections
    const vueSubjects = subjects.map(subject => {
      const subjectSections = sections.filter(s => s.subjectId === subject.id);
      let vueType: string = subject.type;
      if (subject.type === 'credit') vueType = 'Credit';
      else if (subject.type === 'debit') vueType = 'Debit';
      else if (subject.type === 'calculated') vueType = 'Calced';

      return {
        id: subject.id,
        Name: subject.name,
        Type: vueType,
        Order: subject.order,
        IncomePartGoal: subject.goalPercentage || 0,
        Sections: subjectSections.map(section => ({
          id: section.id,
          Name: section.name,
          Order: section.order,
          IncomePartGoal: section.incomePartGoal || 0,
        })),
      };
    });

    const budgetData = {
      Name: budget.name,
      IntervalType: budget.intervalType,
      Active: budget.isActive,
      Subjects: vueSubjects,
      CreatedBy: simpleUser.uid,
      CreatedDate: serverTimestamp(),
      UpdatedDate: serverTimestamp(),
    };

    // Use 'Budgets' (capitalized) to match Vue's collection name
    await setDoc(doc(db, 'companies', currentCompany.id, 'Budgets', id), budgetData);
    return id;
  }, [currentCompany?.id, simpleUser?.uid]);

  const updateBudget = useCallback(async (budgetId: string, updates: Partial<Budget>) => {
    if (!currentCompany?.id) throw new Error('No company selected');

    setIsSaving(true);
    try {
      const updateData: Record<string, unknown> = {
        UpdatedDate: serverTimestamp(),
      };

      // Map our fields to original field names
      if (updates.name !== undefined) updateData.Name = updates.name;
      if (updates.intervalType !== undefined) updateData.IntervalType = updates.intervalType;
      if (updates.isActive !== undefined) updateData.Active = updates.isActive;
      if (updates.description !== undefined) updateData.Description = updates.description;
      if (updates.currency !== undefined) updateData.Currency = updates.currency;

      // Vue app stores dates in DateRange.Start / DateRange.End format
      if (updates.startDate !== undefined || updates.endDate !== undefined) {
        updateData.DateRange = {
          Start: updates.startDate || currentBudget?.startDate || '',
          End: updates.endDate || currentBudget?.endDate || '',
        };
      }

      // Vue stores sections NESTED inside subjects - convert back to that format
      if (updates.subjects !== undefined || updates.sections !== undefined) {
        // Get current state, apply updates
        const newSubjects = updates.subjects || currentBudget?.subjects || [];
        const newSections = updates.sections || currentBudget?.sections || [];

        // Convert to Vue format with nested sections
        updateData.Subjects = newSubjects.map(subject => {
          // Find sections belonging to this subject
          const subjectSections = newSections.filter(s => s.subjectId === subject.id);

          // Map type back to Vue format
          let vueType: string = subject.type;
          if (subject.type === 'credit') vueType = 'Credit';
          else if (subject.type === 'debit') vueType = 'Debit';
          else if (subject.type === 'calculated') vueType = 'Calced';

          return {
            id: subject.id,
            Name: subject.name,
            Type: vueType,
            Order: subject.order,
            // For credit subjects, goalPercentage holds total income value
            IncomePartGoal: subject.goalPercentage || 0,
            // Nest sections inside subject
            Sections: subjectSections.map(section => ({
              id: section.id,
              Name: section.name,
              Order: section.order,
              IncomePartGoal: section.incomePartGoal || 0,
            })),
          };
        });
      }

      // Use 'Budgets' (capitalized) to match Vue's collection name
      await setDoc(doc(db, 'companies', currentCompany.id, 'Budgets', budgetId), updateData, { merge: true });

      // Update current budget if it's the one being updated
      if (currentBudget?.id === budgetId) {
        setCurrentBudget(prev => prev ? { ...prev, ...updates } : null);
      }
    } finally {
      setIsSaving(false);
    }
  }, [currentCompany?.id, currentBudget]);

  const deleteBudget = useCallback(async (budgetId: string) => {
    if (!currentCompany?.id) throw new Error('No company selected');

    // Use 'Budgets' (capitalized) to match Vue's collection name
    await deleteDoc(doc(db, 'companies', currentCompany.id, 'Budgets', budgetId));
    if (currentBudget?.id === budgetId) {
      setCurrentBudget(null);
    }
  }, [currentCompany?.id, currentBudget?.id]);

  // Helper to update _Data field in budget document (Vue app stores entries here)
  const updateBudgetData = useCallback(async (newDataArray: any[]) => {
    if (!currentCompany?.id || !currentBudget?.id) return;

    const budgetRef = doc(db, 'companies', currentCompany.id, 'Budgets', currentBudget.id);
    await setDoc(budgetRef, {
      _Data: JSON.stringify(newDataArray),
      UpdatedDate: serverTimestamp(),
    }, { merge: true });
  }, [currentCompany?.id, currentBudget?.id]);

  const createEntry = useCallback(async (entry: Omit<BudgetEntry, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    if (!currentCompany?.id || !currentBudget) throw new Error('No company or budget selected');

    // Get the interval type to determine which field to use for period
    const intervalType = currentBudget.intervalType || 'monthly';
    const periodField = intervalType.toLowerCase().replace('ly', '');

    // Parse current _Data
    const budgetWithRaw = currentBudget as Budget & { _rawData?: string };
    const currentData = budgetWithRaw._rawData ? JSON.parse(budgetWithRaw._rawData) : [];

    // Create new raw entry in Vue format
    const newRawEntry: any = {
      subject: entry.subjectId,
      section: entry.sectionId,
      [periodField]: entry.period,
      [entry.measure]: entry.amount,
    };

    // Check if entry already exists for this subject/section/period
    const existingIndex = currentData.findIndex((d: any) =>
      d.subject === entry.subjectId &&
      d.section === entry.sectionId &&
      d[periodField]?.toString() === entry.period
    );

    if (existingIndex >= 0) {
      // Update existing entry with new measure value
      currentData[existingIndex][entry.measure] = entry.amount;
    } else {
      // Add new entry
      currentData.push(newRawEntry);
    }

    // Save to Firestore
    await updateBudgetData(currentData);

    // Update local entries state immediately
    const id = generateId();
    const newEntry: BudgetEntry = {
      id,
      ...entry,
      createdAt: new Date(),
      updatedAt: new Date(),
    } as BudgetEntry;
    setEntries(prev => [...prev, newEntry]);

    return id;
  }, [currentCompany?.id, currentBudget, updateBudgetData]);

  const updateEntry = useCallback(async (entryId: string, updates: Partial<BudgetEntry>) => {
    if (!currentCompany?.id || !currentBudget) throw new Error('No company or budget selected');

    // Find the entry to update
    const entry = entries.find(e => e.id === entryId);
    if (!entry) throw new Error('Entry not found');

    // Get the interval type to determine which field to use for period
    const intervalType = currentBudget.intervalType || 'monthly';
    const periodField = intervalType.toLowerCase().replace('ly', '');

    // Parse current _Data
    const budgetWithRaw = currentBudget as Budget & { _rawData?: string };
    const currentData = budgetWithRaw._rawData ? JSON.parse(budgetWithRaw._rawData) : [];

    // Find and update the entry in raw data
    const rawIndex = currentData.findIndex((d: any) =>
      d.subject === (entry as any).subject &&
      d.section === (entry as any).section &&
      d[periodField]?.toString() === entry.period
    );

    if (rawIndex >= 0 && updates.amount !== undefined) {
      currentData[rawIndex][entry.measure] = updates.amount;
      await updateBudgetData(currentData);
    }

    // Update local entries state immediately
    setEntries(prev => prev.map(e =>
      e.id === entryId ? { ...e, ...updates, updatedAt: new Date() } : e
    ));
  }, [currentCompany?.id, currentBudget, entries, updateBudgetData]);

  const deleteEntry = useCallback(async (entryId: string) => {
    if (!currentCompany?.id || !currentBudget) throw new Error('No company or budget selected');

    // Find the entry to delete
    const entry = entries.find(e => e.id === entryId);
    if (!entry) throw new Error('Entry not found');

    // Get the interval type to determine which field to use for period
    const intervalType = currentBudget.intervalType || 'monthly';
    const periodField = intervalType.toLowerCase().replace('ly', '');

    // Parse current _Data
    const budgetWithRaw = currentBudget as Budget & { _rawData?: string };
    const currentData = budgetWithRaw._rawData ? JSON.parse(budgetWithRaw._rawData) : [];

    // Find and remove the entry from raw data
    const rawIndex = currentData.findIndex((d: any) =>
      d.subject === (entry as any).subject &&
      d.section === (entry as any).section &&
      d[periodField]?.toString() === entry.period
    );

    if (rawIndex >= 0) {
      // Remove the measure value, or the entire entry if it was the only measure
      delete currentData[rawIndex][entry.measure];
      // Check if entry still has any measure values
      const measures = currentBudget.measures || ['בפועל', 'מתוכנן'];
      const hasValues = measures.some(m => currentData[rawIndex][m] !== undefined);
      if (!hasValues) {
        currentData.splice(rawIndex, 1);
      }
      await updateBudgetData(currentData);
    }

    // Update local entries state immediately
    setEntries(prev => prev.filter(e => e.id !== entryId));
  }, [currentCompany?.id, currentBudget, entries, updateBudgetData]);

  const getEntriesForPeriod = useCallback((period: string) => {
    return entries.filter(e => e.period === period);
  }, [entries]);

  // Subject management
  const addSubject = useCallback((subject: Omit<BudgetSubject, 'id'>) => {
    if (!currentBudget) return;
    const newSubject = { ...subject, id: generateId() };
    updateBudget(currentBudget.id, {
      subjects: [...(currentBudget.subjects || []), newSubject]
    });
  }, [currentBudget, updateBudget]);

  const updateSubject = useCallback((subjectId: string, updates: Partial<BudgetSubject>) => {
    if (!currentBudget) return;
    const updatedSubjects = currentBudget.subjects.map(s =>
      s.id === subjectId ? { ...s, ...updates } : s
    );
    updateBudget(currentBudget.id, { subjects: updatedSubjects });
  }, [currentBudget, updateBudget]);

  const removeSubject = useCallback((subjectId: string) => {
    if (!currentBudget) return;
    updateBudget(currentBudget.id, {
      subjects: currentBudget.subjects.filter(s => s.id !== subjectId)
    });
  }, [currentBudget, updateBudget]);

  // Section management
  const addSection = useCallback((section: Omit<BudgetSection, 'id'>) => {
    if (!currentBudget) return;
    const newSection = { ...section, id: generateId() };
    updateBudget(currentBudget.id, {
      sections: [...(currentBudget.sections || []), newSection]
    });
  }, [currentBudget, updateBudget]);

  const updateSection = useCallback((sectionId: string, updates: Partial<BudgetSection>) => {
    if (!currentBudget) return;
    const updatedSections = currentBudget.sections.map(s =>
      s.id === sectionId ? { ...s, ...updates } : s
    );
    updateBudget(currentBudget.id, { sections: updatedSections });
  }, [currentBudget, updateBudget]);

  const removeSection = useCallback((sectionId: string) => {
    if (!currentBudget) return;
    updateBudget(currentBudget.id, {
      sections: currentBudget.sections.filter(s => s.id !== sectionId)
    });
  }, [currentBudget, updateBudget]);

  // Helper function to generate _Data entries for a project (same as Vue's calcContentProject)
  const generateProjectDataEntries = useCallback((
    startDate: string,
    endDate: string,
    subjects: any[],
    measures: any[],
    intervalType: string = 'Monthly'
  ): any[] => {
    const dataEntries: any[] = [];

    if (!startDate || !endDate || !subjects?.length || !measures?.length) {
      return dataEntries;
    }

    let tmpDate = new Date(startDate);
    const end = new Date(endDate);

    while (tmpDate < end) {
      const month = `${tmpDate.getMonth() + 1}/${tmpDate.getFullYear().toString().substring(2)}`;
      const week = getWeekNumber(tmpDate);

      // For each subject (excluding Calced type)
      subjects.filter((s: any) => s.Type !== 'Calced').forEach((subject: any) => {
        const sections = subject.Sections || [];

        if (sections.length > 0) {
          // For each section in the subject
          sections.forEach((section: any) => {
            const row: any = {};

            // Set period based on interval type
            if (intervalType === 'Weekly') {
              row['week'] = week;
            } else {
              row['month'] = month;
            }

            row['subject'] = subject.Name;
            row['section'] = section.Name;

            // Add each measure with empty value
            measures.forEach((measure: any) => {
              row[measure.Name] = '';
            });

            dataEntries.push({ ...row });
          });
        } else {
          // Subject without sections
          const row: any = {};

          if (intervalType === 'Weekly') {
            row['week'] = week;
          } else {
            row['month'] = month;
          }

          row['subject'] = subject.Name;
          row['section'] = subject.Name; // Use subject name as section

          measures.forEach((measure: any) => {
            row[measure.Name] = '';
          });

          dataEntries.push({ ...row });
        }
      });

      // Increment date based on interval type
      if (intervalType === 'Weekly') {
        tmpDate.setDate(tmpDate.getDate() + 7);
      } else {
        tmpDate.setMonth(tmpDate.getMonth() + 1);
      }
    }

    return dataEntries;
  }, []);

  // Helper to get week number
  const getWeekNumber = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  // Project CRUD - Projects are independent entities at company level
  const createProject = useCallback(async (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    if (!currentCompany?.id) throw new Error('No company selected');

    // Build Subjects structure from project's own subjects or use defaults
    const projectSubjects = project.subjects?.map(subject => {
      const subjectSections = project.sections?.filter(s => s.subjectId === subject.id) || [];
      return {
        id: subject.id,
        Name: subject.name,
        Type: subject.type === 'credit' ? 'Credit' : subject.type === 'debit' ? 'Debit' : 'Calced',
        IncomePartGoal: subject.goalPercentage || 0,
        Sections: subjectSections.map(sec => ({
          id: sec.id,
          Name: sec.name,
          IncomePartGoal: sec.incomePartGoal || 0,
        })),
      };
    }) || [
      // Default subjects if none provided
      { id: 'income', Name: 'הכנסות', Type: 'Credit', IncomePartGoal: 0, Sections: [] },
      { id: 'expenses', Name: 'הוצאות', Type: 'Debit', IncomePartGoal: 0, Sections: [] },
      { id: 'profit', Name: 'רווח', Type: 'Calced', IncomePartGoal: 0, Sections: [] },
    ];

    // Build Measures structure
    const projectMeasures = project.measures?.map(m => ({
      Name: m === 'actual' ? 'בפועל' : m === 'budget' ? 'יעד' : m,
    })) || [{ Name: 'בפועל' }, { Name: 'יעד' }];

    // Generate _Data entries for all periods/subjects/sections/measures
    const intervalType = project.intervalType || 'monthly';
    const dataEntries = generateProjectDataEntries(
      project.startDate,
      project.endDate,
      projectSubjects,
      projectMeasures,
      intervalType
    );

    const id = generateId();

    // Build document in Vue format
    const projectDoc = {
      Document: {
        Name: project.name,
        CreatedDate: serverTimestamp(),
        UpdateDate: serverTimestamp(),
      },
      Client: project.client || '',
      PM: project.manager || '', // PM = Manager in Vue
      PManager: project.projectManager || '', // PManager = Project Manager in Vue
      Contract: project.contractNumber || '',
      Status: project.status === 'active' ? 1 : project.status === 'completed' ? 2 : 3,
      DateRange: {
        Start: project.startDate,
        End: project.endDate,
      },
      IntervalType: intervalType,
      Currency: project.currency || 'ILS',
      SpreadType: 'Rows',
      Subjects: projectSubjects,
      Measures: projectMeasures,
      _Data: JSON.stringify(dataEntries),
      BudgetId: project.budgetId || null, // Optional reference to a budget
      CreatedDate: serverTimestamp(),
      UpdateDate: serverTimestamp(),
    };

    await setDoc(doc(db, 'companies', currentCompany.id, 'Projects', id), projectDoc);
    return id;
  }, [currentCompany?.id, generateProjectDataEntries]);

  const updateProject = useCallback(async (projectId: string, updates: Partial<Project>) => {
    if (!currentCompany?.id) throw new Error('No company selected');

    // Convert updates to Vue format
    const vueUpdates: any = {
      UpdateDate: serverTimestamp(),
    };

    if (updates.name !== undefined) {
      vueUpdates['Document.Name'] = updates.name;
    }
    if (updates.client !== undefined) {
      vueUpdates.Client = updates.client;
    }
    if (updates.projectManager !== undefined) {
      vueUpdates.PManager = updates.projectManager; // PManager = Project Manager
    }
    if (updates.manager !== undefined) {
      vueUpdates.PM = updates.manager; // PM = Manager
    }
    if (updates.contractNumber !== undefined) {
      vueUpdates.Contract = updates.contractNumber;
    }
    if (updates.status !== undefined) {
      vueUpdates.Status = updates.status === 'active' ? 1 : updates.status === 'completed' ? 2 : 3;
    }
    if (updates.startDate !== undefined) {
      vueUpdates['DateRange.Start'] = updates.startDate;
    }
    if (updates.endDate !== undefined) {
      vueUpdates['DateRange.End'] = updates.endDate;
    }
    if (updates.goals !== undefined) {
      // Convert goals to Subjects format
      // Find the current project to get existing Subjects structure
      const currentProject = projects.find(p => p.id === projectId);
      if (currentProject) {
        // We need to update the Subjects with new goal values
        // For now, just store goals separately - full Subjects update requires more complex logic
      }
    }

    await setDoc(doc(db, 'companies', currentCompany.id, 'Projects', projectId),
      vueUpdates,
      { merge: true }
    );
  }, [currentCompany?.id, projects]);

  const deleteProject = useCallback(async (projectId: string) => {
    if (!currentCompany?.id) throw new Error('No company selected');

    await deleteDoc(doc(db, 'companies', currentCompany.id, 'Projects', projectId));
  }, [currentCompany?.id]);

  // Regenerate project data collection (same as Vue's "GENERATE DATA COLLECTION")
  const regenerateProjectData = useCallback(async (projectId: string, preserveExisting: boolean = true) => {
    if (!currentCompany?.id) throw new Error('No company selected');

    const project = projects.find(p => p.id === projectId);
    if (!project) throw new Error('Project not found');

    // Build Subjects structure from project's own subjects (Vue format)
    const projectSubjects = project.subjects?.map(subject => {
      const subjectSections = project.sections?.filter(s => s.subjectId === subject.id) || [];
      return {
        id: subject.id,
        Name: subject.name,
        Type: subject.type === 'credit' ? 'Credit' : subject.type === 'debit' ? 'Debit' : 'Calced',
        IncomePartGoal: subject.goalPercentage || 0,
        Sections: subjectSections.map(sec => ({
          id: sec.id,
          Name: sec.name,
          IncomePartGoal: sec.incomePartGoal || 0,
        })),
      };
    }) || [];

    // Build Measures structure from project
    const projectMeasures = project.measures?.map(m => ({
      Name: m === 'actual' ? 'בפועל' : m === 'budget' ? 'יעד' : m,
    })) || [{ Name: 'בפועל' }, { Name: 'יעד' }];

    // Generate new _Data entries
    const newDataEntries = generateProjectDataEntries(
      project.startDate,
      project.endDate,
      projectSubjects,
      projectMeasures,
      project.intervalType || 'monthly'
    );

    let finalData = newDataEntries;

    // If preserving existing data, merge with existing entries
    if (preserveExisting) {
      const existingEntries = projectEntries.filter(e => e.projectId === projectId);
      if (existingEntries.length > 0) {
        // Merge existing values into new structure
        finalData = newDataEntries.map(newRow => {
          const period = newRow.month || newRow.week;
          const subject = newRow.subject;
          const section = newRow.section;

          // Find matching existing entries
          const matchingEntries = existingEntries.filter(e =>
            e.period === period &&
            e.subjectId === subject &&
            (!section || e.sectionId === section)
          );

          // Copy values from existing entries
          matchingEntries.forEach(existing => {
            const measureName = existing.measure === 'actual' ? 'בפועל' :
                               existing.measure === 'budget' ? 'יעד' : existing.measure;
            if (existing.amount && existing.amount !== 0) {
              newRow[measureName] = existing.amount.toString();
            }
          });

          return newRow;
        });
      }
    }

    // Update project with new structure
    await setDoc(doc(db, 'companies', currentCompany.id, 'Projects', projectId), {
      Subjects: projectSubjects,
      Measures: projectMeasures,
      _Data: JSON.stringify(finalData),
      UpdateDate: serverTimestamp(),
    }, { merge: true });
  }, [currentCompany?.id, projects, projectEntries, generateProjectDataEntries]);

  // Get project subjects and sections
  const getProjectSubjects = useCallback((projectId: string): { subjects: BudgetSubject[]; sections: BudgetSection[] } => {
    const project = projects.find(p => p.id === projectId);
    if (!project) {
      return { subjects: [], sections: [] };
    }

    // Return project's own subjects/sections
    return {
      subjects: project.subjects || [],
      sections: project.sections || [],
    };
  }, [projects]);

  // Add subject to a project
  const addProjectSubject = useCallback(async (projectId: string, subject: Omit<BudgetSubject, 'id'>) => {
    if (!currentCompany?.id) throw new Error('No company selected');

    const projectRef = doc(db, 'companies', currentCompany.id, 'Projects', projectId);

    // First get the current project data
    const projectSnapshot = await getDocs(query(collection(db, 'companies', currentCompany.id, 'Projects'), where('__name__', '==', projectId)));

    let currentSubjects: any[] = [];
    if (!projectSnapshot.empty) {
      const data = projectSnapshot.docs[0].data();
      currentSubjects = data.Subjects || [];
    }

    // Add new subject in Vue format
    const newSubject = {
      id: generateId(),
      Name: subject.name,
      Type: subject.type === 'credit' ? 'Credit' : subject.type === 'debit' ? 'Debit' : 'Calced',
      Order: subject.order || currentSubjects.length,
      IncomePartGoal: subject.goalPercentage || 0,
      Sections: [],
    };

    await setDoc(projectRef, {
      Subjects: [...currentSubjects, newSubject],
      UpdateDate: serverTimestamp(),
    }, { merge: true });
  }, [currentCompany?.id, currentBudget?.id]);

  // Update a subject in a project
  const updateProjectSubject = useCallback(async (projectId: string, subjectId: string, updates: Partial<BudgetSubject>) => {
    if (!currentCompany?.id) throw new Error('No company selected');

    const projectRef = doc(db, 'companies', currentCompany.id, 'Projects', projectId);

    // First get the current project data
    const projectSnapshot = await getDocs(query(collection(db, 'companies', currentCompany.id, 'Projects'), where('__name__', '==', projectId)));

    if (projectSnapshot.empty) return;

    const data = projectSnapshot.docs[0].data();
    const currentSubjects = data.Subjects || [];

    // Find and update the subject
    const updatedSubjects = currentSubjects.map((s: any) => {
      if (s.id === subjectId || s.Name === subjectId) {
        return {
          ...s,
          ...(updates.name !== undefined && { Name: updates.name }),
          ...(updates.type !== undefined && { Type: updates.type === 'credit' ? 'Credit' : updates.type === 'debit' ? 'Debit' : 'Calced' }),
          ...(updates.order !== undefined && { Order: updates.order }),
          ...(updates.goalPercentage !== undefined && { IncomePartGoal: updates.goalPercentage }),
        };
      }
      return s;
    });

    await setDoc(projectRef, {
      Subjects: updatedSubjects,
      UpdateDate: serverTimestamp(),
    }, { merge: true });
  }, [currentCompany?.id, currentBudget?.id]);

  // Remove a subject from a project
  const removeProjectSubject = useCallback(async (projectId: string, subjectId: string) => {
    if (!currentCompany?.id) throw new Error('No company selected');

    const projectRef = doc(db, 'companies', currentCompany.id, 'Projects', projectId);

    // First get the current project data
    const projectSnapshot = await getDocs(query(collection(db, 'companies', currentCompany.id, 'Projects'), where('__name__', '==', projectId)));

    if (projectSnapshot.empty) return;

    const data = projectSnapshot.docs[0].data();
    const currentSubjects = data.Subjects || [];

    // Remove the subject
    const updatedSubjects = currentSubjects.filter((s: any) => s.id !== subjectId && s.Name !== subjectId);

    await setDoc(projectRef, {
      Subjects: updatedSubjects,
      UpdateDate: serverTimestamp(),
    }, { merge: true });
  }, [currentCompany?.id, currentBudget?.id]);

  // Add section to a project subject
  const addProjectSection = useCallback(async (projectId: string, section: Omit<BudgetSection, 'id'>) => {
    if (!currentCompany?.id) throw new Error('No company selected');

    const projectRef = doc(db, 'companies', currentCompany.id, 'Projects', projectId);

    // First get the current project data
    const projectSnapshot = await getDocs(query(collection(db, 'companies', currentCompany.id, 'Projects'), where('__name__', '==', projectId)));

    if (projectSnapshot.empty) return;

    const data = projectSnapshot.docs[0].data();
    const currentSubjects = data.Subjects || [];

    // Find the subject and add the section
    const updatedSubjects = currentSubjects.map((s: any) => {
      if (s.id === section.subjectId || s.Name === section.subjectId) {
        const newSection = {
          id: generateId(),
          Name: section.name,
          Order: section.order || (s.Sections?.length || 0),
          IncomePartGoal: section.incomePartGoal || 0,
        };
        return {
          ...s,
          Sections: [...(s.Sections || []), newSection],
        };
      }
      return s;
    });

    await setDoc(projectRef, {
      Subjects: updatedSubjects,
      UpdateDate: serverTimestamp(),
    }, { merge: true });
  }, [currentCompany?.id, currentBudget?.id]);

  // Update a section in a project
  const updateProjectSection = useCallback(async (projectId: string, sectionId: string, updates: Partial<BudgetSection>) => {
    if (!currentCompany?.id) throw new Error('No company selected');

    const projectRef = doc(db, 'companies', currentCompany.id, 'Projects', projectId);

    // First get the current project data
    const projectSnapshot = await getDocs(query(collection(db, 'companies', currentCompany.id, 'Projects'), where('__name__', '==', projectId)));

    if (projectSnapshot.empty) return;

    const data = projectSnapshot.docs[0].data();
    const currentSubjects = data.Subjects || [];

    // Find the section in subjects and update it
    const updatedSubjects = currentSubjects.map((s: any) => {
      const sections = s.Sections || [];
      const sectionIndex = sections.findIndex((sec: any) => sec.id === sectionId || sec.Name === sectionId);
      if (sectionIndex >= 0) {
        const updatedSections = [...sections];
        updatedSections[sectionIndex] = {
          ...updatedSections[sectionIndex],
          ...(updates.name !== undefined && { Name: updates.name }),
          ...(updates.order !== undefined && { Order: updates.order }),
          ...(updates.incomePartGoal !== undefined && { IncomePartGoal: updates.incomePartGoal }),
        };
        return { ...s, Sections: updatedSections };
      }
      return s;
    });

    await setDoc(projectRef, {
      Subjects: updatedSubjects,
      UpdateDate: serverTimestamp(),
    }, { merge: true });
  }, [currentCompany?.id, currentBudget?.id]);

  // Remove a section from a project
  const removeProjectSection = useCallback(async (projectId: string, sectionId: string) => {
    if (!currentCompany?.id) throw new Error('No company selected');

    const projectRef = doc(db, 'companies', currentCompany.id, 'Projects', projectId);

    // First get the current project data
    const projectSnapshot = await getDocs(query(collection(db, 'companies', currentCompany.id, 'Projects'), where('__name__', '==', projectId)));

    if (projectSnapshot.empty) return;

    const data = projectSnapshot.docs[0].data();
    const currentSubjects = data.Subjects || [];

    // Find the section in subjects and remove it
    const updatedSubjects = currentSubjects.map((s: any) => {
      const sections = s.Sections || [];
      return {
        ...s,
        Sections: sections.filter((sec: any) => sec.id !== sectionId && sec.Name !== sectionId),
      };
    });

    await setDoc(projectRef, {
      Subjects: updatedSubjects,
      UpdateDate: serverTimestamp(),
    }, { merge: true });
  }, [currentCompany?.id, currentBudget?.id]);

  // Reorder subjects in a project
  const reorderProjectSubjects = useCallback(async (projectId: string, subjects: BudgetSubject[]) => {
    if (!currentCompany?.id) throw new Error('No company selected');

    const projectRef = doc(db, 'companies', currentCompany.id, 'Projects', projectId);

    // Convert to Vue format
    const vueSubjects = subjects.map((s, idx) => ({
      id: s.id,
      Name: s.name,
      Type: s.type === 'credit' ? 'Credit' : s.type === 'debit' ? 'Debit' : 'Calced',
      Order: idx,
      IncomePartGoal: s.goalPercentage || 0,
      Sections: [], // Preserve sections would require fetching current data
    }));

    await setDoc(projectRef, {
      Subjects: vueSubjects,
      UpdateDate: serverTimestamp(),
    }, { merge: true });
  }, [currentCompany?.id, currentBudget?.id]);

  // Update project entries (stored in _Data field)
  const updateProjectEntries = useCallback(async (
    projectId: string,
    entries: Array<{ period: string; subjectId: string; sectionId?: string; measure: string; amount: number }>
  ) => {
    if (!currentCompany?.id) throw new Error('No company selected');

    // Convert entries to the original _Data format with measure
    const dataEntries = entries.map(entry => ({
      period: entry.period,
      subject: entry.subjectId,
      section: entry.sectionId || '',
      measure: entry.measure,
      amount: entry.amount,
    }));

    await setDoc(doc(db, 'companies', currentCompany.id, 'Projects', projectId), {
      _Data: JSON.stringify(dataEntries),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }, [currentCompany?.id, currentBudget?.id]);

  // Project filter setter
  const setProjectFilters = useCallback((filters: Partial<ProjectFilters>) => {
    setProjectFiltersState(prev => ({ ...prev, ...filters }));
  }, []);

  // Filtered projects based on current filters
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      // Search filter (name, client, PM, manager, contract)
      if (projectFilters.search) {
        const searchLower = projectFilters.search.toLowerCase();
        const matchesSearch =
          project.name.toLowerCase().includes(searchLower) ||
          project.client.toLowerCase().includes(searchLower) ||
          project.projectManager.toLowerCase().includes(searchLower) ||
          project.manager.toLowerCase().includes(searchLower) ||
          project.contractNumber.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Status filter
      if (projectFilters.status !== 'all' && project.status !== projectFilters.status) {
        return false;
      }

      // Project Manager filter
      if (projectFilters.projectManager && project.projectManager !== projectFilters.projectManager) {
        return false;
      }

      // Manager filter
      if (projectFilters.manager && project.manager !== projectFilters.manager) {
        return false;
      }

      // Client filter
      if (projectFilters.client && project.client !== projectFilters.client) {
        return false;
      }

      // Contract filter
      if (projectFilters.contract && project.contractNumber !== projectFilters.contract) {
        return false;
      }

      // Date range filter
      if (projectFilters.dateFrom && project.startDate < projectFilters.dateFrom) {
        return false;
      }
      if (projectFilters.dateTo && project.endDate > projectFilters.dateTo) {
        return false;
      }

      return true;
    });
  }, [projects, projectFilters]);

  // Calculate project progress indicators
  const calculateProjectProgress = useCallback((project: Project) => {
    // Time consumed: percentage of time elapsed between start and end dates
    const calcTimeConsumed = (): number => {
      if (!project.startDate || !project.endDate) return 0;
      const start = new Date(project.startDate).getTime();
      const end = new Date(project.endDate).getTime();
      const now = Date.now();
      const total = end - start;
      if (total <= 0) return 0;
      const elapsed = now - start;
      return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
    };

    // Get project entries
    const entries = projectEntries.filter(e => e.projectId === project.id);

    // Helper to check subject type from budget
    const getSubjectType = (subjectName: string) => {
      const subject = currentBudget?.subjects.find(s => s.name === subjectName || s.id === subjectName);
      return subject?.type;
    };

    // Helper to check measure type
    const isActualMeasure = (measure: string) => {
      const m = measure.toLowerCase();
      return m === 'actual' || m === 'בפועל' || m.includes('בפועל');
    };

    const isBudgetMeasure = (measure: string) => {
      const m = measure.toLowerCase();
      return m === 'budget' || m === 'מתוכנן' || m.includes('מתוכנן');
    };

    // Calculate actual income (credit subjects with actual measure)
    const actualIncome = entries
      .filter(e => getSubjectType(e.subjectId) === 'credit' && isActualMeasure(e.measure))
      .reduce((sum, e) => sum + e.amount, 0);

    // Calculate actual expenses (debit subjects with actual measure)
    const actualExpenses = entries
      .filter(e => getSubjectType(e.subjectId) === 'debit' && isActualMeasure(e.measure))
      .reduce((sum, e) => sum + e.amount, 0);

    // Calculate budgeted expenses
    const budgetedExpenses = entries
      .filter(e => getSubjectType(e.subjectId) === 'debit' && isBudgetMeasure(e.measure))
      .reduce((sum, e) => sum + e.amount, 0);

    // Budget consumed: actual expenses / budgeted expenses
    const budgetConsumed = budgetedExpenses > 0
      ? Math.round((actualExpenses / budgetedExpenses) * 100)
      : 0;

    // Invoices issued: actual income / project budget
    const invoicesIssued = project.budget > 0
      ? Math.round((actualIncome / project.budget) * 100)
      : 0;

    // Profit calculations
    const profit = actualIncome - actualExpenses;
    const profitPercent = actualIncome > 0
      ? Math.round((profit / actualIncome) * 100)
      : 0;

    return {
      timeConsumed: calcTimeConsumed(),
      budgetConsumed,
      invoicesIssued,
      profit,
      profitPercent,
    };
  }, [projectEntries, currentBudget?.subjects]);

  // Calculations - use the second measure (typically 'מתוכנן'/budget/planned) by default
  const calculateSummary = useCallback((period?: string, measure?: MeasureType) => {
    // Default to the second measure in the budget's measures array (typically budget/planned)
    // Or the first one if only one exists
    const defaultMeasure = currentBudget?.measures?.[1] || currentBudget?.measures?.[0] || 'מתוכנן';
    const targetMeasure = measure || defaultMeasure;

    // Match both exact measure name and common variations
    const matchesMeasure = (entryMeasure: string) => {
      if (entryMeasure === targetMeasure) return true;
      // Map English to Hebrew
      if (targetMeasure === 'budget' && (entryMeasure === 'מתוכנן' || entryMeasure.includes('מתוכנן'))) return true;
      if (targetMeasure === 'actual' && (entryMeasure === 'בפועל' || entryMeasure.includes('בפועל'))) return true;
      return false;
    };

    const filteredEntries = period
      ? entries.filter(e => e.period === period && matchesMeasure(e.measure))
      : entries.filter(e => matchesMeasure(e.measure));

    let totalIncome = 0;
    let totalExpenses = 0;

    filteredEntries.forEach(entry => {
      // Try to find subject by ID first, then by name (for entries that used name as fallback)
      const subject = currentBudget?.subjects.find(s =>
        s.id === entry.subjectId || s.name === entry.subjectId
      );

      if (subject?.type === 'credit') {
        totalIncome += entry.amount;
      } else if (subject?.type !== 'calculated') {
        // Debit or unknown type = expense (excluding calculated fields like Profit)
        totalExpenses += entry.amount;
      }
    });

    return {
      totalIncome,
      totalExpenses,
      profit: totalIncome - totalExpenses,
    };
  }, [entries, currentBudget?.subjects, currentBudget?.measures]);

  const getPeriodsList = useCallback(() => {
    const periods = new Set(entries.map(e => e.period));
    // Sort numerically (periods are typically week/month numbers)
    return Array.from(periods).sort((a, b) => parseInt(a) - parseInt(b));
  }, [entries]);

  // Create data collection - generates budget entries based on subjects/sections and date range
  // Similar to Vue's calcContent() - creates empty entries for data input
  const createDataCollection = useCallback(async () => {
    if (!currentCompany?.id || !currentBudget?.id) {
      throw new Error('No company or budget selected');
    }

    const budget = currentBudget;
    const intervalType = (budget.intervalType || 'monthly').toLowerCase();
    const isWeekly = intervalType === 'weekly';
    const periodField = isWeekly ? 'week' : 'month';

    // Calculate periods from date range (like Vue's calcContent)
    let periods: number[] = [];

    // Check for valid dates (not empty strings)
    const hasValidDates = budget.startDate && budget.endDate &&
      budget.startDate.length > 0 && budget.endDate.length > 0;

    if (hasValidDates) {
      const startDate = new Date(budget.startDate);
      const endDate = new Date(budget.endDate);

      // Validate dates are valid
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        const tmpDate = new Date(startDate);

        while (tmpDate < endDate) {
          if (isWeekly) {
            // Get week number
            const onejan = new Date(tmpDate.getFullYear(), 0, 1);
            const week = Math.ceil((((tmpDate.getTime() - onejan.getTime()) / 86400000) + onejan.getDay() + 1) / 7);
            periods.push(week);
            tmpDate.setDate(tmpDate.getDate() + 7);
          } else {
            // Get month number (1-12)
            periods.push(tmpDate.getMonth() + 1);
            tmpDate.setMonth(tmpDate.getMonth() + 1);
          }
        }
      }
    }

    // Fallback if no valid periods generated
    if (periods.length === 0) {
      const numPeriods = isWeekly ? 52 : 12;
      for (let i = 1; i <= numPeriods; i++) {
        periods.push(i);
      }
    }

    console.log('Creating data collection with periods:', periods, 'intervalType:', intervalType);

    // Get measures from budget (Hebrew: בפועל = Actual, מתוכנן = Planned)
    const measures = budget.measures || ['בפועל', 'מתוכנן'];

    // Helper to check if a measure name is the planned/budget measure
    const isPlannedMeasure = (m: string) => {
      const lower = m.toLowerCase();
      return lower === 'מתוכנן' || lower === 'planned' || lower === 'budget';
    };

    // Find the planned measure by name (not by position - order can vary)
    const foundPlannedMeasure = measures.find(m => {
      const name = typeof m === 'string' ? m : (m as any).Name || '';
      return isPlannedMeasure(name);
    });
    const plannedMeasure = foundPlannedMeasure
      ? (typeof foundPlannedMeasure === 'string' ? foundPlannedMeasure : (foundPlannedMeasure as any).Name || 'מתוכנן')
      : 'מתוכנן';

    // Calculate total income from credit subjects (goalPercentage field holds the total income value)
    const totalIncome = budget.subjects
      .filter(s => s.type === 'credit')
      .reduce((sum, s) => sum + (s.goalPercentage || 0), 0);

    const periodsCount = periods.length;

    console.log('Total income for planned calculations:', totalIncome, 'periods:', periodsCount);

    // Helper to calculate planned amount for a section based on incomePartGoal percentage
    const calcPlannedAmount = (incomePartGoal: number | undefined): number => {
      if (!totalIncome || !periodsCount || !incomePartGoal) return 0;
      return (totalIncome * incomePartGoal / 100) / periodsCount;
    };

    // Generate data entries for each period and subject/section combination
    // Like Vue's calcContent + calcContentBudgetGoals - creates entries with planned amounts
    const dataEntries: any[] = [];

    periods.forEach(period => {
      budget.subjects.forEach(subject => {
        const subjectSections = budget.sections.filter(s => s.subjectId === subject.id);

        if (subjectSections.length > 0) {
          // Subject has sections
          subjectSections.forEach(section => {
            const entry: any = {
              [periodField]: period,
              subject: subject.name,
              section: section.name,
            };
            // Add values for each measure
            measures.forEach(measure => {
              const measureName = typeof measure === 'string' ? measure : (measure as any).Name || measure;
              // For planned measure, calculate based on incomePartGoal percentage
              if (measureName === plannedMeasure && section.incomePartGoal) {
                entry[measureName] = calcPlannedAmount(section.incomePartGoal);
              } else {
                entry[measureName] = '';
              }
            });
            dataEntries.push(entry);
          });
        } else {
          // Subject has no sections - use subject's goalPercentage directly for credit subjects
          const entry: any = {
            [periodField]: period,
            subject: subject.name,
            section: '',
          };
          measures.forEach(measure => {
            const measureName = typeof measure === 'string' ? measure : (measure as any).Name || measure;
            // For credit subjects without sections, use goalPercentage as the planned amount per period
            if (measureName === plannedMeasure && subject.type === 'credit' && subject.goalPercentage) {
              entry[measureName] = subject.goalPercentage / periodsCount;
            } else if (measureName === plannedMeasure && subject.goalPercentage) {
              // For debit subjects, calculate based on percentage of income
              entry[measureName] = calcPlannedAmount(subject.goalPercentage);
            } else {
              entry[measureName] = '';
            }
          });
          dataEntries.push(entry);
        }
      });
    });

    // Save to Firestore
    const budgetRef = doc(db, 'companies', currentCompany.id, 'Budgets', currentBudget.id);
    await setDoc(budgetRef, {
      _Data: JSON.stringify(dataEntries),
      UpdatedDate: serverTimestamp(),
    }, { merge: true });

    console.log(`Created ${dataEntries.length} data entries for budget ${currentBudget.id}`);
  }, [currentCompany?.id, currentBudget]);

  const value = useMemo(() => ({
    companies,
    currentCompany,
    selectCompany,
    budgets,
    currentBudget,
    entries,
    projects,
    projectEntries,
    projectFilters,
    setProjectFilters,
    filteredProjects,
    calculateProjectProgress,
    isLoading,
    isSaving,
    selectBudget,
    createBudget,
    updateBudget,
    deleteBudget,
    createEntry,
    updateEntry,
    deleteEntry,
    getEntriesForPeriod,
    addSubject,
    updateSubject,
    removeSubject,
    addSection,
    updateSection,
    removeSection,
    createProject,
    updateProject,
    deleteProject,
    regenerateProjectData,
    addProjectSubject,
    updateProjectSubject,
    removeProjectSubject,
    addProjectSection,
    updateProjectSection,
    removeProjectSection,
    reorderProjectSubjects,
    getProjectSubjects,
    updateProjectEntries,
    calculateSummary,
    getPeriodsList,
    createDataCollection,
    selectedPeriod,
    setSelectedPeriod,
    selectedMeasures,
    setSelectedMeasures,
  }), [
    companies,
    currentCompany,
    selectCompany,
    budgets,
    currentBudget,
    entries,
    projects,
    projectEntries,
    projectFilters,
    setProjectFilters,
    filteredProjects,
    calculateProjectProgress,
    isLoading,
    isSaving,
    selectBudget,
    createBudget,
    updateBudget,
    deleteBudget,
    createEntry,
    updateEntry,
    deleteEntry,
    getEntriesForPeriod,
    addSubject,
    updateSubject,
    removeSubject,
    addSection,
    updateSection,
    removeSection,
    createProject,
    updateProject,
    deleteProject,
    regenerateProjectData,
    addProjectSubject,
    updateProjectSubject,
    removeProjectSubject,
    addProjectSection,
    updateProjectSection,
    removeProjectSection,
    reorderProjectSubjects,
    getProjectSubjects,
    updateProjectEntries,
    calculateSummary,
    getPeriodsList,
    createDataCollection,
    selectedPeriod,
    selectedMeasures,
  ]);

  return (
    <BudgetContext.Provider value={value}>
      {children}
    </BudgetContext.Provider>
  );
};

export const useBudget = () => {
  const context = useContext(BudgetContext);
  if (!context) {
    throw new Error('useBudget must be used within a BudgetProvider');
  }
  return context;
};
